/*globals $:false */

import tmpl_dialog from '../templates/upload-dialog.dot';
import tmpl_error_msg from '../templates/upload-errormsg.dot';
import tmpl_msg from '../templates/upload-msg.dot';

import ui from '@cob/ui-genesis/cob.ui.js';
import utils from '@cob/ui-genesis/cob.utils.js';

var fileUpload = {};

//**Usage**:
//instantiate a `new cob.fileUpload.UploadFileP(core, callbacks, opts)` and then start it passing in
//the `url` of the target post endpoint
//
//**Parameters**:
//`core` the cob object
//`callbacks` an object with the following supported callbacks:
//      `uploadSuccessful(fileName, data)`: called when the file is successfully uploaded
//      `uploadFailed(fileName, data)`: called when the file upload fails
//      `validateFileName`: validate the chosen file name
//      `getInvalidFilenameMessage`: get the message to show when the file name is invalid
//      `validateMessage`: validate the message written by the user (only used when the showMessageField opt is true)
//      `cancel`: (optional) called when the cancel button is clicked
//
//`opts`: optional parameters
//      `allowedFileExtensions`: an array of file extensions that can be uploaded
//      `closeOnSuccess`: [true | false] close the upload dialog when the file upload succeeds? (defaults to true)
//      `closeOnFail`: [true | false] close the upload dialog when the file upload fails? (defaults to true)
//      `showDropZone`: [true | false] enable the drag 'n drop funcionality on the upload dialog?
//                      (Its default value varies with your browser compatibilities. Ex: IE8 does not support drag and drop)
//      `showMessageField`: [true | false] the upload dialog must show a message textarea where the message is mandatory? (defaults to false)
//      `dialogTitle`: A custom title for the upload dialog
//      `timeout`: The upload timeout
//      `tmpl`: The .dot template to use for the upload dialog
//
//**Example**:
//```
//var _fileUploadDialog = new cob.fileUpload.UploadFileP(
//      core,
//      {
//          uploadSuccessful: function(fileName, data) {
//              //do stuff
//          },
//
//          uploadFailed: function(fileName, data) {
//          //do stuff
//          }
//      },
//      {
//          "allowedFileExtensions": ["zip","tar"],
//            "dialogTitle": {
//               "fileName": "cob.file",
//               "translation": "title"
//            }
//      }
//);
//_fileUploadDialog.start(targetUrl);
//```
fileUpload.UploadFileP = function(core, cbs, opts) {
    var _callbacks = cbs || {};

    var _useDropZone = (window.FileReader ? true : false),
        _opts = {
            closeOnSuccess: true,
            closeOnFail: true,
            showDropZone: _useDropZone,
            tmpl: tmpl_dialog,
            dialogTitle: core.translateString("cob.file-upload", "file-upload.upload-file")
        };

    utils.extend(_opts, opts, true);

    var _core = core,
        _formData,
        _fileName,
        _uploadFileV,
        _baseErrorMessage = core.translateString("cob.file-upload", "error.uploading-file-error"),
        _baseInfoMessage = core.translateString("cob.file-upload", "upload-file-success"),
        _loadingKey = core.getLoadingKey();


    var _fileUploadedSuccess = function(response) {
        if (_callbacks.uploadSuccessful) {
            _callbacks.uploadSuccessful(_fileName, response);
        } else {
            ui.notification.showInfo(_baseInfoMessage.replace("__FILENAME__", _fileName));
        }
    };

    var _fileUploadFailed = function(response) {
        if (_callbacks.uploadFailed) {
            _callbacks.uploadFailed(_fileName, response);
        } else {
            ui.notification.showError(_baseErrorMessage.replace("__FILENAME__", _fileName));
        }
    };

    // Validate the filename size
    var _fileIsValid = function(file) {
        return file && file.length > 0;
    };

    // Validate the file extensions
    var _validFileExtension = function(file) {
        var isOk = false;

        if (_opts.allowedFileExtensions && _opts.allowedFileExtensions.length > 0) {
            _opts.allowedFileExtensions.forEach(function(extension) {
                if (!isOk) {
                    if (file.lastIndexOf('.' + extension) >= 0) {
                        isOk = true;
                    }
                }
            });
            return isOk;
        }

        return true;
    };

    var _validateMessage = function(message) {
        if (_opts.showMessageField && _callbacks.validateMessage && typeof _callbacks.validateMessage === "function") {
            return _callbacks.validateMessage(message);
        }

        return true;
    };

    var _validateData = function(data) {
        var fileName = data.file,
            message = data.message,
            fileExists = _fileIsValid(fileName),
            fileExtensionIsValid = _validFileExtension(fileName),
            messageIsValid = _validateMessage(message),
            invalidFileNameMessage;

        var fileNameIsValid = _callbacks.validateFileName && typeof _callbacks.validateFileName === "function"
                ? _callbacks.validateFileName(fileName)
                : true;

        if (fileExists && fileNameIsValid && fileExtensionIsValid && messageIsValid) {
            core.showLoading(_loadingKey, 'file upload ' + fileName);

            _uploadFileV.removeErrors();

            _formData.formData = {};

            if (data.message) {
                _formData.formData.message = data.message;
            }

            if (!_useDropZone) {
                // When uploading a file with an iframe in IE8, the filename
                // has the fullpath of the file without the slashes, so we send the filename
                // in the formdata.
                _formData.formData.filename = fileName;
            }

            _formData.submit();

            return true;

        } else {
            if (!fileExists) {
                _uploadFileV.showMissingFileError();
            }

            if(!fileNameIsValid){

                if(_callbacks.getInvalidFilenameMessage && typeof _callbacks.getInvalidFilenameMessage === "function"){
                    invalidFileNameMessage = _callbacks.getInvalidFilenameMessage();

                } else {
                    invalidFileNameMessage = core.translateString("cob.file-upload", "error.invalid-file-name");
                }

                _uploadFileV.showErrors([invalidFileNameMessage]);
            }

            if (fileExists && !fileExtensionIsValid) {
                _uploadFileV.showFileExtensionError(_opts.allowedFileExtensions);
            }

            if (!messageIsValid) {
                _uploadFileV.showMissingMessageError();
            }

            return false;
        }
    };

    return{
        getContainer: function() {
            return _uploadFileV.getContainer();
        },
        start: function(url) {
            _callbacks.validateData = _validateData;

            _uploadFileV = new fileUpload.UploadFileV(core, _callbacks, _opts);

            var dropZoneTimeout,
                addDropZoneIndicator = _useDropZone,
                fileUploadConfig = {
                    url: url,
                    autoUpload: false,

                    headers: {
                       "x-cob-tz": Intl.DateTimeFormat().resolvedOptions().timeZone
                    },

                    // If the browser does not support the HTML5 Drag'n'Drop API, we disable the dropzone
                    // and force the browser to use the IFrameTransport to encapsulate the form data
                    // and send it to the server
                    dropZone: (_useDropZone ? _uploadFileV.getDropZone() : $()),
                    forceIframeTransport: !_useDropZone,
                    dataType: "html",
                    dragover: function() {
                        _uploadFileV.showDropZone();
                        if (addDropZoneIndicator) {
                            addDropZoneIndicator = false;
                        } else {
                            //If the file is still being dragged inside the dropzone,
                            // the timeout is renewed and the dropzone indicator does not disappear
                            clearTimeout(dropZoneTimeout);
                        }

                        // Create a 100 ms timeout
                        //After the user is 100 ms out of the dropzone
                        // the dropzone indicator disappears
                        dropZoneTimeout = setTimeout(function() {
                            _uploadFileV.hideDropZone();
                            addDropZoneIndicator = true;
                        }, 100);
                    },
                    add: function(e, data) {
                        if (data.files) {
                            _formData = data;

                            _fileName = data.files[0].name;
                            if (_fileName.length > 0) {
                                _uploadFileV.showUploadedFile(_fileName);
                            }
                        }
                    },
                    done: function(e, data) {
                        _fileUploadedSuccess(data);

                        if (_opts.closeOnSuccess) _uploadFileV.hide();
                    },
                    fail: function(e, data) {
                        _fileUploadFailed(data);

                        if (_opts.closeOnFail) _uploadFileV.hide();
                    },
                    always: function() {
                        _core.hideLoading(_loadingKey, 'file upload');
                    }
                };

            if (opts && opts.timeout) {
                fileUploadConfig.timeout = opts.timeout;
            }

            _uploadFileV.configureFileUpload(fileUploadConfig);
            _uploadFileV.show();
        },
        showErrors: function(errors) {
            _uploadFileV.showErrors(errors);
        },
        showSuccessMsg: function(msg) {
            _uploadFileV.showSuccessMsg(msg);
        },
        stop: function() {
            _uploadFileV.hide();
        },
        hideUploadForm: function (){
            _uploadFileV.hideUploadForm();
        },
        hideUploadBtn: function (){
            _uploadFileV.hideUploadBtn();
        }
    };
};

//opts:
//  tmpl: An optional dot template for the upload file dialog
//
// callbacks:
//`cancel`: (optional) called when the cancel button is clicked
//`validateData`: the function used by the UploadFileP to execute the required data validations
fileUpload.UploadFileV = function(core, callbacks, opts) {
    var _uploadDialog = $(opts.tmpl(opts)),
        _dialogBody = $(".modal-body", _uploadDialog),
        _fileInput = $(".js-fileuploader", _uploadDialog),
        _fileNameContainer = $(".js-filename", _uploadDialog),
        _confirmButton = $(".js-confirm", _uploadDialog),
        _cancelButton = $(".js-cancel", _uploadDialog),
        _dropzoneIndicator = $(".dropzone-indicator", _uploadDialog),
        _extraFields = $(".js-extra-fields", _uploadDialog),
        _errorsList = $(".js-errors-list", _uploadDialog),
        _successMsgContainer = $(".js-success-msg-container", _uploadDialog),
        _missingFileError = core.translateString("cob.file-upload", "error.uploading-file-missing"),
        _fileInvalidExtensionError = core.translateString("cob.file-upload", "error.uploading-file-invalid-extension"),
        _missingMessageError = core.translateString("cob.file-upload", "error.uploading-message-missing"),
        _dragNDropHelpMsg,
        _msgArea;

    var _destroyDialog = function() {
        _uploadDialog.modal("hide");
        _uploadDialog.on('hidden', function() {
            _cleanUpDialog();
            _uploadDialog.remove();
            $(window).unbind('resize.file-upload', _onResize);
        });
    };

    var _cleanUpDialog = function() {
        _removeFileName();

        if (opts.showMessageField) {
            _msgArea.val("")
        }

        _removeErrors();
        _removeSuccessMsg();
    };

    var _removeErrors = function(errorType) {
        if (errorType) {
            $("[data-type=" + errorType + "]", _errorsList).remove();
        } else {
            _errorsList.html("");
        }
    };

    var _addError = function(errorType, errorMsg, keepPreviousErrors) {

        //Sometimes we need to keep the previous errors of the required type visible
        //By default it will remove them
        if (!keepPreviousErrors) {
            $("[data-type=" + errorType + "]", _errorsList).remove();
        }

        _errorsList.append(tmpl_error_msg({type: errorType, msg: errorMsg}));
    };

    var _showSuccessMsg = function(msg) {
        _successMsgContainer.text(msg);
        _successMsgContainer.removeClass("hidden");
    };

    var _removeSuccessMsg = function() {
        _successMsgContainer.text("");
        _successMsgContainer.addClass("hidden");
    };

    // Show the name of the uploaded file (remove the error message if needed)
    var _showFileName = function(fileName) {
        _removeSuccessMsg();
        _removeErrors("missing-file");

        _enableConfirmBtn(true);

        _fileNameContainer.text(fileName);
        _fileNameContainer.removeClass("hidden");

        if (opts.showDropZone) {
            _dragNDropHelpMsg.addClass("hidden");
        }
    };

    var _removeFileName = function() {
        _fileNameContainer.text("");
        _fileNameContainer.addClass("hidden");

        if (opts.showDropZone) {
            _dragNDropHelpMsg.removeClass("hidden");
        }
    };

    var _configureFileUpload = function(fileUploadConfig) {
        _fileInput.fileupload(fileUploadConfig);
    };

    var _getDropZone = function() {
        return _dialogBody;
    };

    var _showDropZone = function() {
        _dropzoneIndicator.removeClass("hidden");
    };

    var _hideDropZone = function() {
        _dropzoneIndicator.addClass("hidden");
    };

    var _addOptionalFields = function() {

        if (opts.showDropZone) {
            _dragNDropHelpMsg = $(".js-drag-n-drop-help-msg", _uploadDialog);
            _dragNDropHelpMsg.removeClass("hidden");
        }

        if (opts.showMessageField) {
            _extraFields.append(tmpl_msg());
            _msgArea = $(".js-upload-msg", _extraFields);
        }
    };

    //Hack to center the upload dialog when the window resizes
    var _onResize = function() {
        var windowWidth = $(window).width(),
            dialogMinWidthString = _uploadDialog.css("min-width") || "450px",
            dialogMinWidth = parseInt(dialogMinWidthString.substring(0, dialogMinWidthString.indexOf("px")), 10),
            dialogWidth = _uploadDialog.is(":visible") ? _uploadDialog.width() : dialogMinWidth;

        _uploadDialog.css({
            "margin-left": (windowWidth - dialogWidth) / 2
        });

        //We let the erros list grow until it almost fills the entire screen (the dialog will grow with it)
        _errorsList.css("max-width", windowWidth - 50);
    };

    var _enableConfirmBtn = function(enableBtn){
        _confirmButton.enable(enableBtn);
    };

    var _addEvenListeners = function() {
        var cancelFn = callbacks.cancel || _destroyDialog;

        $(window).bind('resize.file-upload', _onResize);

        $(".js-upload", _uploadDialog).click(function(e) {
            e.preventDefault();
            _fileInput.click();
        });

        if (callbacks.validateData && typeof callbacks.validateData === 'function') {
            _confirmButton.click(function() {
                var data = {
                    file: _fileNameContainer.text()
                };
                if (opts.showMessageField) {
                    data.message = _msgArea.val();
                }

                _removeSuccessMsg();

                //If all the data is valid, we disable the confirm button while the request is processed
                if(callbacks.validateData(data)){
                    _enableConfirmBtn(false);
                }
            });
        }

        if (opts.showMessageField && callbacks.validateMessage && typeof callbacks.validateMessage === "function") {
            _msgArea.blur(function() {
                if (callbacks.validateMessage && typeof callbacks.validateMessage === "function") {
                    if (callbacks.validateMessage(_msgArea.val())) {
                        _removeErrors("missing-msg");
                    } else {
                        _addError("missing-msg", _missingMessageError);
                    }
                }
            });
        }

        _cancelButton.click(cancelFn);
    };

    (function init() {

        _addOptionalFields();

        _addEvenListeners();

        core.translate('cob.file-upload', _uploadDialog);

    })();

    return {
        show: function() {
            _uploadDialog.modal({show: true, cobAppInstanceId: core.getAppInstanceId()});
            $(window).trigger('resize.file-upload');
        },
        hide: _destroyDialog,
        getContainer: function() {return _uploadDialog},
        showMissingFileError: function() {
            _addError("missing-file", _missingFileError);

            //Hack to center the upload dialog when there are errors
            $(window).trigger('resize.file-upload');
        },
        showFileExtensionError: function(allowedExtensions) {
            _addError("file-extension",
                _fileInvalidExtensionError.replace("__FILEEXTENSION__", allowedExtensions.join(",")));

            //Hack to center the upload dialog when there are errors
            $(window).trigger('resize.file-upload');
        },
        showMissingMessageError: function() {
            _addError("missing-msg", _missingMessageError);

            //Hack to center the upload dialog when there are errors
            $(window).trigger('resize.file-upload');
        },
        showErrors: function(errors) {
            errors.forEach(function(error) {
                _addError("custom", error, true);
            });

            //Hack to center the upload dialog when there are errors
            $(window).trigger('resize.file-upload');
        },
        showSuccessMsg: _showSuccessMsg,
        removeErrors: _removeErrors,
        showUploadedFile: _showFileName,
        configureFileUpload: _configureFileUpload,
        getDropZone: _getDropZone,
        showDropZone: _showDropZone,
        hideDropZone: _hideDropZone,
        enableConfirmBtn: _enableConfirmBtn,
        hideUploadForm: function(){
            $("#uploadform", _uploadDialog).addClass("hidden");
        },
        hideUploadBtn: function() {
            _confirmButton.addClass("hidden");
        }
    };
};

export default fileUpload;
